import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import { Input } from '../../../../../../../components/ui/input';
import { Button } from '../../../../../../../components/ui/button';
import { cn } from '../../../../../../../lib/utils';

interface CollapsibleFilterBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  activeFilterCount: number;
  filteredCount?: number;
  filteredCountLabel?: string;
  onResetFilters: () => void;
  children: React.ReactNode;
}

export const CollapsibleFilterBar: React.FC<CollapsibleFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  activeFilterCount,
  filteredCount,
  filteredCountLabel = 'results',
  onResetFilters,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4">
      <div className="flex items-center gap-3">
        {/* Search — always visible */}
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-heritage-grey pointer-events-none" />
          <Input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 h-9 text-[13px] placeholder:text-heritage-grey"
          />
        </div>

        {/* Filters toggle */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setExpanded((e) => !e)}
          className={cn(
            'h-9 flex items-center gap-2 text-[12px] font-medium border-[#E2E5EA] text-text-grey hover:bg-page-bg',
            expanded && 'bg-page-bg border-heritage-grey'
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-hero-red text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform text-heritage-grey',
              expanded && 'rotate-180'
            )}
          />
        </Button>

        {/* Clear all — only if active */}
        {activeFilterCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="h-9 text-[12px] text-readable-red hover:text-readable-red hover:bg-red-50 flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Clear all
          </Button>
        )}

        {/* Result count */}
        {typeof filteredCount === 'number' && (
          <span className="text-[12px] text-heritage-grey whitespace-nowrap">
            {filteredCount} {filteredCountLabel}
          </span>
        )}
      </div>

      {/* Expandable filters panel */}
      {expanded && (
        <div className="mt-3 bg-white border border-[#E2E5EA] rounded-lg p-3">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 [&>*]:min-w-0">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};
